import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BaseCard from './BaseCard';
import {
  STATUS_TO_BE_LOCATED,
  STATUS_LOCATED,
  STATUS_TO_BE_FIXED,
  STATUS_FIXED,
  FONT_FAMILY
} from '../../../dashboardColors';
import './WorkflowTrendCard.scss';

const WorkflowTrendCard = ({ data, t }) => {
  // Use real data or empty array
  const workflowData = data || [];

  // State for visible series
  const [visibleSeries, setVisibleSeries] = useState({
    toBeLocated: true,
    located: true,
    toBeFixed: true,
    fixed: true
  });

  const handleLegendClick = (key) => {
    setVisibleSeries(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Custom legend component for better control with new status colors
  const CustomLegend = () => (
    <Box display="flex" justifyContent="center" flexWrap="wrap" mt={1} gap={1.5}>
      {[
        { key: 'toBeLocated', color: STATUS_TO_BE_LOCATED, label: t('to_be_located') },
        { key: 'located', color: STATUS_LOCATED, label: t('located') },
        { key: 'toBeFixed', color: STATUS_TO_BE_FIXED, label: t('to_be_fixed') },
        { key: 'fixed', color: STATUS_FIXED, label: t('fixed') }
      ].map(({ key, color, label }) => (
        <Typography
          key={key}
          variant="caption"
          display="flex"
          alignItems="center"
          onClick={() => handleLegendClick(key)}
          sx={{
            cursor: 'pointer',
            opacity: visibleSeries[key] ? 1 : 0.4,
            textDecoration: visibleSeries[key] ? 'none' : 'line-through',
            fontWeight: 700
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 3,
              backgroundColor: color,
              marginRight: 0.5
            }}
          />
          {label}
        </Typography>
      ))}
    </Box>
  );

  return (
    <BaseCard
      title={t('workflow_trend_quarterly')}
      icon={<TrendingUpIcon />}
      className="workflow-trend-card"
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box sx={{ flex: 1, minHeight: 160, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={workflowData}
              margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 14, fontWeight: 600, fontFamily: FONT_FAMILY, fill: '#666' }}
                stroke="#666"
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                tick={{ fontSize: 14, fontWeight: 600, fontFamily: FONT_FAMILY, fill: '#666' }}
                stroke="#666"
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY
                }}
              />
              {visibleSeries.toBeLocated && (
                <Line
                  type="linear"
                  dataKey="toBeLocated"
                  stroke={STATUS_TO_BE_LOCATED}
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: STATUS_TO_BE_LOCATED, strokeWidth: 0 }}
                  activeDot={{ r: 4, fill: STATUS_TO_BE_LOCATED, strokeWidth: 0 }}
                  name={t('to_be_located')}
                />
              )}
              {visibleSeries.located && (
                <Line
                  type="linear"
                  dataKey="located"
                  stroke={STATUS_LOCATED}
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: STATUS_LOCATED, strokeWidth: 0 }}
                  activeDot={{ r: 4, fill: STATUS_LOCATED, strokeWidth: 0 }}
                  name={t('located')}
                />
              )}
              {visibleSeries.toBeFixed && (
                <Line
                  type="linear"
                  dataKey="toBeFixed"
                  stroke={STATUS_TO_BE_FIXED}
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: STATUS_TO_BE_FIXED, strokeWidth: 0 }}
                  activeDot={{ r: 4, fill: STATUS_TO_BE_FIXED, strokeWidth: 0 }}
                  name={t('to_be_fixed')}
                />
              )}
              {visibleSeries.fixed && (
                <Line
                  type="linear"
                  dataKey="fixed"
                  stroke={STATUS_FIXED}
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: STATUS_FIXED, strokeWidth: 0 }}
                  activeDot={{ r: 4, fill: STATUS_FIXED, strokeWidth: 0 }}
                  name={t('fixed')}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <CustomLegend />
        <Typography variant="body2" align="left" sx={{ fontStyle: 'italic', fontSize: '0.5rem', color: '#666', mt: 1, px: 1 }}>
          * {t('to_be_fixed_note')}
        </Typography>
      </Box>
    </BaseCard>
  );
};

WorkflowTrendCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    quarter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    toBeLocated: PropTypes.number,
    located: PropTypes.number,
    toBeFixed: PropTypes.number,
    fixed: PropTypes.number
  })),
  t: PropTypes.func.isRequired
};

export default WorkflowTrendCard;
